import React, {useMemo, useRef} from 'react';
import {Box, HStack, Icon, IconButton, Input, Text, VStack} from "@chakra-ui/react";
import {ChevronLeft, ChevronRight, FilePlus2, X} from 'lucide-react';
import objectUtil from "@/utils/objectUtil";
import {useApi} from "@/uses/useApi";
import {useModal} from "@/contexts/modalContext";
import {NameFiles} from "@/components";
import {useLanguage} from "@/contexts/languageContext";

export const FilesInput = ({files, onChange, accept, deleteText, noLabel = false, multiple = true, containerProps = {}, ...props}) => {

    const {language, t} = useLanguage();
    const {open, close} = useModal();
    /** Key is the uploaded file url, this allows value to be compared directly to the input file object
     * because otherwise, the url key would be missing from input file object and deepEqual would be false
     * */
    const uploadedFilesRef = useRef({});

    const fileInputRef = useRef();
    const api = useApi();

    const CustomIcon = useMemo(() => {
        return <IconButton
            onClick={() => fileInputRef?.current?.click?.()}
            backgroundColor='secondary.500'
            width={256}
        >
            <Text>{t('sentences.selectFiles')}</Text>
            <Icon as={FilePlus2} color="gray.550" boxSize={5}/>
        </IconButton>;
    }, [fileInputRef])

    const upload = async (tempFiles) => {
        if (tempFiles.length === 0) {
            throw new Error('INVALID_UPLOAD');
        }

        const nonExistingFiles = Array.from(tempFiles)
            .map((file) => ({
                file,
                fileInfo: objectUtil.getFileInfo(file)
            }))
            .filter(({file, fileInfo}) => {
                return Object.keys(uploadedFilesRef.current).findIndex((key) => {
                    return objectUtil.deepEqual(uploadedFilesRef.current[key], fileInfo);
                }) === -1;
            })

        if (nonExistingFiles.length > 0) {
            /** Get a presigned url to allow uploading to miniIO from front */
            api.FileService.getPresignedURL(nonExistingFiles.map(({file}) => file)).then(async (res) => {
                if (res?.data?.type === 'success' && objectUtil.isArray(res?.data?.value)) {
                    const promises = [];
                    for (const index in res.data.value) {
                        if (!res.data.value[index]?.presignedURL) {
                            throw new Error('MISSING_PRESIGNED_URL');
                        }
                        if (!res.data.value[index]?.url) {
                            throw new Error('MISSING_FILE_URL');
                        }

                        promises.push(api.FileService.upload(res.data.value[index].presignedURL, tempFiles.item(index)).then(() => {
                            uploadedFilesRef.current = {
                                ...uploadedFilesRef.current,
                                [res.data.value[index].url]: nonExistingFiles[index].fileInfo
                            }
                        }).catch(console.error));
                    }
                    await Promise.all(promises);
                    if (noLabel === false) {
                        // Ajouter les URLs locales pour l'aperçu des images
                        const filesWithPreview = res.data.value.map((fileData, idx) => ({
                            ...fileData,
                            previewUrl: URL.createObjectURL(tempFiles.item(idx))
                        }));
                        open(
                            <NameFiles
                                onSuccess={(newFiles) => {
                                    // Libérer les URLs d'objet créées
                                    filesWithPreview.forEach(f => URL.revokeObjectURL(f.previewUrl));
                                    if (multiple) {
                                        onChange([...(files ?? []), ...newFiles]);
                                    } else {
                                        onChange(newFiles);
                                    }
                                }}
                                close={() => {
                                    // Libérer les URLs d'objet si on ferme sans valider
                                    filesWithPreview.forEach(f => URL.revokeObjectURL(f.previewUrl));
                                    close();
                                }}
                                files={filesWithPreview}
                            />, 'cover', {}, true, 'addFilesInput');
                    } else {
                        const newUrls = (res?.data?.value ?? []).map((e) => e.url);
                        if (multiple) {
                            onChange([...(files ?? []), ...newUrls]);
                        } else {
                            onChange(newUrls);
                        }
                    }
                }
            }).catch(console.error)
        }
    };

    const moveFile = (index, direction) => {
        const newFiles = [...files];
        if (direction === 'left' && index > 0) {
            [newFiles[index - 1], newFiles[index]] = [newFiles[index], newFiles[index - 1]];
        } else if (direction === 'right' && index < newFiles.length - 1) {
            [newFiles[index + 1], newFiles[index]] = [newFiles[index], newFiles[index + 1]];
        }
        onChange(newFiles);
    };

    const getFilename = (url) => {
        return url.split('/').pop();
    }

    return (<Box
        padding=".5rem"
        borderRadius="1rem"
        position="relative"
        display="flex"
        justifyContent="space-between"
        gap="1rem"
        flexDirection={"column"}
        {...props}
    >
        <Input
            ref={fileInputRef}
            type="file"
            accept={accept ?? "image/*"}
            onChange={(e) => {
                upload(e?.target?.files ?? []).catch(console.error)
            }}
            display="none"
            multiple={multiple}
        />
        {((files ?? []).length === 0 || multiple) && CustomIcon}

        <HStack
            w="100%"
            overflowX={'auto'}
            spacing="4"
        >
            {((files ?? []).length > 0) && files.map((file, index) => <HStack key={`file-${noLabel ? file : file.url}`}
                                                                     justifyContent="space-between"
                                                                     alignItems="center">
                <VStack>
                    <Box position="relative">
                        <img src={noLabel ? file : file.url} alt={getFilename(noLabel ? file : file.url)} width={128} style={{borderRadius: '8px'}}/>
                        <Box
                            position="absolute"
                            top="4px"
                            left="4px"
                            bg="primary.500"
                            color="white"
                            borderRadius="full"
                            w="24px"
                            h="24px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            fontSize="xs"
                            fontWeight="bold"
                        >
                            {index + 1}
                        </Box>
                    </Box>
                    <HStack>
                        {multiple && (
                            <>
                                <IconButton
                                    variant="ghost"
                                    aria-label="Move left"
                                    isDisabled={index === 0}
                                    onClick={() => moveFile(index, 'left')}
                                    size="sm"
                                >
                                    <Icon as={ChevronLeft} />
                                </IconButton>
                                <IconButton
                                    variant="ghost"
                                    aria-label="Move right"
                                    isDisabled={index === files.length - 1}
                                    onClick={() => moveFile(index, 'right')}
                                    size="sm"
                                >
                                    <Icon as={ChevronRight} />
                                </IconButton>
                            </>
                        )}
                    </HStack>
                    <HStack>
                        <p
                            style={{
                                overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis'
                            }}>
                            {noLabel ? getFilename(file) : file.label?.[language] ?? 'Undefined filename'}
                        </p>
                        <IconButton
                            variant="dark"
                            aria-label={t('sentences.deleteFile')}
                            isRound={true}
                            sx={{
                                "& > *": {
                                    pointerEvents: "none", // Disable pointer events for all child elements
                                }
                            }}
                            onClick={() => {
                                const updatedFiles = [...files];
                                updatedFiles.splice(index, 1);
                                onChange(updatedFiles);
                                if (fileInputRef?.current?.value) {
                                    fileInputRef.current.value = '';
                                }
                            }}>
                            <Icon as={X} color="base.500" boxSize={5}/>
                        </IconButton>
                    </HStack>
                </VStack>
            </HStack>)}
        </HStack>
    </Box>);
};

export default FilesInput;
